import "./PlayerForm.css";
import React, { useState, useEffect } from "react";
import { useGame, usePlayer } from "../api/copenhunt/v1/copenhunt_rbt_react";
import { getPlayersByTeam } from "../utils";
import { PlayerInfo } from "../api/copenhunt/v1/copenhunt_pb";

interface PlayerFormProps {
  gameId: string;
  newPlayerId: string;
}

const PlayerForm: React.FC<PlayerFormProps> = ({ gameId, newPlayerId }) => {
  const { useOverview } = useGame({ id: gameId });
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [playerName, setPlayerName] = useState("");
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerInfo | null>(null);
  const [step, setStep] = useState<"team" | "player">("team");
  const { mutators: playerMutators } = usePlayer({ id: newPlayerId });
  const { response, isLoading, aborted } = useOverview({});
  const [playersByTeam, setPlayersByTeam] = useState<
    Record<string, PlayerInfo[]>
  >({});

  useEffect(() => {
    if (response?.playerInfos && response?.teamInfoById) {
      const teamIds = Object.keys(response.teamInfoById);
      const validPlayers = response.playerInfos.filter(
        (player) => player.name !== ""
      );
      setPlayersByTeam(getPlayersByTeam(validPlayers, teamIds));
    }
  }, [response]);

  const handleTeamSelection = (teamId: string) => {
    setSelectedTeam(teamId);
    setStep("player");
  };

  const handlePlayerSelection = (player: PlayerInfo) => {
    setSelectedPlayer(player);
    setPlayerName(""); // Clear new player name
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPlayerName(event.target.value);
    setSelectedPlayer(null); // Clear selected player
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (selectedTeam) {
      if (playerName) {
        await playerMutators.create({ name: playerName, teamId: selectedTeam });
        window.location.href = `/game/${gameId}/player/${newPlayerId}`;
      } else if (selectedPlayer) {
        window.location.href = `/game/${gameId}/player/${selectedPlayer.playerId}`;
      }
    }
  };

  const handleBack = () => {
    setStep("team");
    setSelectedTeam(null);
    setSelectedPlayer(null);
    setPlayerName("");
  };

  if (isLoading) return <div>Loading...</div>;
  if (aborted) return <div>Error loading game data.</div>;

  return (
    <div className="player-form-container">
      {step === "team" ? (
        <div>
          <h2>Select a Team</h2>
          <div className="team-selection">
            {response?.teamInfoById &&
              Object.entries(response.teamInfoById).map(
                ([teamId, teamInfo]) => (
                  <div
                    key={teamId}
                    className="team-card"
                    onClick={() => handleTeamSelection(teamId)}
                  >
                    <h4>{teamInfo.name}</h4>
                    <ul>
                      {playersByTeam[teamId]?.map((player) => (
                        <li key={player.playerId}>{player.name}</li>
                      ))}
                    </ul>
                  </div>
                )
              )}
          </div>
        </div>
      ) : (
        selectedTeam && (
          <div>
            <button onClick={handleBack} className="back-button">
              Back to Team Selection
            </button>
            <h2>
              Team: {response?.teamInfoById[selectedTeam]?.name || "Selected"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <h3>Select a player or add a new one</h3>
                <div className="player-selection">
                  {playersByTeam[selectedTeam]?.map((player) => (
                    <div
                      key={player.playerId}
                      className={`player-card ${
                        selectedPlayer?.playerId === player.playerId
                          ? "selected"
                          : ""
                      }`}
                      onClick={() => handlePlayerSelection(player)}
                    >
                      {player.name}
                    </div>
                  ))}
                  <div
                    className={`player-card ${
                      !selectedPlayer && playerName ? "selected" : ""
                    }`}
                    onClick={() => setSelectedPlayer(null)}
                  >
                    <input
                      type="text"
                      id="playerName"
                      placeholder="Add a new player"
                      value={playerName}
                      onChange={handleNameChange}
                      className="player-name-input"
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={!selectedPlayer && !playerName}
              >
                Join Game
              </button>
            </form>
          </div>
        )
      )}
    </div>
  );
};

export default PlayerForm;
